import { useState, useEffect } from 'react';
import { Table, Avatar, Group, Text, ScrollArea, Badge } from '@mantine/core';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getDailyPeaks } from '../../services/api';
import classes from './DailyPeaks.module.css';

interface DailyPeakVideo {
    videoTitle: string;
    channel: string;
    channelId: string;
    channelAvatar: string;
    views: string; // Picco giornaliero
    totalViews: string;
    timeAgo: string; // Data del picco
    urlVideo: string;
}

function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

export function DailyPeaks() {
    const [videos, setVideos] = useState<DailyPeakVideo[]>([]);
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const truncateTitle = (title: string) => {
        if (!title) return '';
        if (title.length > 60) {
            return title.slice(0, 60) + '...';
        }
        return title;
    };

    const toggleRow = (index: number) => {
        setExpandedRows(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    useEffect(() => {
        async function fetchDailyPeaks() {
            try {
                setLoading(true);
                console.log('Fetching daily peaks...');
                const response = await getDailyPeaks();
                console.log('API Response:', response.data);
                const data = response.data;

                // Ensure data is an array
                if (Array.isArray(data)) {
                    console.log(`Successfully loaded ${data.length} videos`);
                    console.log('First video sample:', data[0]);
                    setVideos(data);
                    setError(null);
                } else {
                    console.error('API did not return an array:', data);
                    setError('Formato dati non valido');
                    setVideos([]);
                }
            } catch (err: any) {
                console.error('Error fetching daily peaks:', err);
                if (err.code === 'ECONNABORTED') {
                    setError('Richiesta scaduta - Il server sta impiegando troppo tempo. Riprova tra poco.');
                } else if (err.response) {
                    setError(`Errore del server: ${err.response.status}`);
                } else if (err.request) {
                    setError('Impossibile connettersi al server. Assicurati che il backend sia in esecuzione.');
                } else {
                    setError('Impossibile caricare i dati');
                }
                setVideos([]);
            } finally {
                setLoading(false);
            }
        }

        fetchDailyPeaks();
    }, []);

    const formatNumber = (num: string) => {
        if (!num) return '0';
        const number = parseInt(num.replace(/\D/g, ''), 10);
        if (isNaN(number)) return '0';
        if (number >= 1000000) {
            return (number / 1000000).toFixed(1) + 'M';
        } else if (number >= 1000) {
            return (number / 1000).toFixed(1) + 'K';
        }
        return number.toString();
    };

    const getMedalClass = (index: number) => {
        if (index === 0) return classes.goldMedal;
        if (index === 1) return classes.silverMedal;
        if (index === 2) return classes.bronzeMedal;
        return '';
    };

    if (loading) {
        return <div className={classes.loading}>Caricamento...</div>;
    }

    if (error) {
        return <div className={classes.error}>{error}</div>;
    }

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <h1 className={classes.title}>🔥 Record di Visualizzazioni in 24 Ore</h1>
                <p className={classes.subtitle}>
                    I video che hanno registrato il maggior numero di visualizzazioni in un singolo giorno
                </p>
            </div>

            <ScrollArea>
                <Table horizontalSpacing="md" verticalSpacing="xs" className={classes.responsiveTable}>
                    <Table.Thead className={classes.th}>
                        <Table.Tr>
                            <Table.Th className={classes.rankColumn}>Rank</Table.Th>
                            <Table.Th>Canale</Table.Th>
                            <Table.Th>Video</Table.Th>
                            <Table.Th className={classes.peakColumn}>
                                <div className={classes.peakHeader}>
                                    <span>Picco 24h</span>
                                </div>
                            </Table.Th>
                            <Table.Th className={classes.totalColumn}>Visualizzazioni Totali</Table.Th>
                            <Table.Th className={classes.dateColumn}>Data Picco</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {videos && videos.map((video, index) => (
                            <Table.Tr key={index} className={classes.tr}>
                                <Table.Td className={classes.rankColumn}>
                                    <div className={classes.rankCell}>
                                        <span className={`${classes.rank} ${getMedalClass(index)}`}>
                                            {index + 1}
                                        </span>
                                    </div>
                                </Table.Td>
                                <Table.Td>
                                    <Group gap="sm">
                                        <Link to={`/channel/${video?.channelId}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'inherit' }}>
                                            <Avatar src={video?.channelAvatar} size="md" radius="xl" />
                                            <Text size="sm" fw={500} className={classes.channelName}>
                                                {video?.channel ? decodeHtmlEntities(video.channel) : ''}
                                            </Text>
                                        </Link>
                                    </Group>
                                </Table.Td>
                                <Table.Td>
                                    <div className={classes.videoCell}>
                                        <a
                                            href={`https://www.youtube.com/watch?v=${video?.urlVideo || ''}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={classes.videoLink}
                                        >
                                            <Text
                                                size="sm"
                                                className={classes.videoTitle}
                                                title={video?.videoTitle ? decodeHtmlEntities(video.videoTitle) : ''}
                                            >
                                                {expandedRows.includes(index)
                                                    ? (video?.videoTitle ? decodeHtmlEntities(video.videoTitle) : '')
                                                    : truncateTitle(video?.videoTitle ? decodeHtmlEntities(video.videoTitle) : '')}
                                            </Text>
                                        </a>
                                        {video?.videoTitle && video?.videoTitle?.length > 60 && (
                                            <button
                                                onClick={() => toggleRow(index)}
                                                className={classes.expandButton}
                                            >
                                                {expandedRows.includes(index) ? (
                                                    <ChevronUp size={16} />
                                                ) : (
                                                    <ChevronDown size={16} />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </Table.Td>
                                <Table.Td className={classes.peakColumn}>
                                    <Badge
                                        size="lg"
                                        variant="filled"
                                        color="red"
                                        className={classes.peakBadge}
                                    >
                                        +{video?.views ? formatNumber(video.views) : '0'}
                                    </Badge>
                                </Table.Td>
                                <Table.Td className={classes.totalColumn}>
                                    <Text size="sm" c="dimmed">
                                        {video?.totalViews ? formatNumber(video.totalViews) : '0'}
                                    </Text>
                                </Table.Td>
                                <Table.Td className={classes.dateColumn}>
                                    <Text size="sm" c="dimmed">
                                        {video?.timeAgo || 'N/A'}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        </div>
    );
}
